import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import get from 'lodash/get';
import noop from 'lodash/noop';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { UnwrapArray } from '@sbv-clients/util-misc';

import {
  Api,
  ProLaborLevelDesignationCacheKeys,
  UpdateProLaborLevelDesignation,
  useApi,
} from '../index';
import {
  DEFAULT_LABOR_LEVEL_NAMES,
  ProLaborLevelDesignationName,
} from '../util/constants';

export type LaborLevelDesignationMap = Record<string, string>;

// Hook to fetch designation names with defaults
export const useProLaborLevelDesignations = () => {
  const api = useApi();

  const queryResult = useQuery<LaborLevelDesignationMap>({
    queryKey: ['proLaborLevelDesignations'],
    queryFn: async () => {
      const res =
        await api.proLaborLevelDesignationApi.proLaborLevelDesignationProDesignationsWithNameGet();

      const list = res.data?.laborLevelDesignations ?? [];

      const result: LaborLevelDesignationMap = {
        ...DEFAULT_LABOR_LEVEL_NAMES,
      };

      for (const item of list) {
        const name =
          item.proLaborLevelDesignationName as ProLaborLevelDesignationName;
        if (name && item.laborLevelName) {
          result[name] = String(item.laborLevelName);
        }
      }

      return result;
    },
    staleTime: 1000 * 60 * 10,
  });

  const { data, isLoading, error } = queryResult;

  return {
    DepartmentLaborLevel:
      data?.[ProLaborLevelDesignationName.DepartmentLaborLevel] ??
      DEFAULT_LABOR_LEVEL_NAMES[
        ProLaborLevelDesignationName.DepartmentLaborLevel
      ],
    PositionLaborLevel:
      data?.[ProLaborLevelDesignationName.PositionLaborLevel] ??
      DEFAULT_LABOR_LEVEL_NAMES[
        ProLaborLevelDesignationName.PositionLaborLevel
      ],
    UnitLaborLevel:
      data?.[ProLaborLevelDesignationName.UnitLaborLevel] ??
      DEFAULT_LABOR_LEVEL_NAMES[ProLaborLevelDesignationName.UnitLaborLevel],
    isLoading,
    error,
  };
};

// Hook to fetch raw list of designations
export const useProLaborLevelDesignationsGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [ProLaborLevelDesignationCacheKeys.ProLaborLevelDesignations],
    queryFn: () =>
      api.proLaborLevelDesignationApi
        .proLaborLevelDesignationGet()
        .then((response) => response?.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export type ProLaborLevelDesignationsResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof useProLaborLevelDesignationsGet>['data']>
>;

// Mutation logic
const updateProLaborLevelDesignation =
  (api: Api) =>
  (
    body: UpdateProLaborLevelDesignation & {
      proLaborLevelDesignationId: number;
    }
  ) => {
    const { proLaborLevelDesignationId, ...updateProLaborLevelDesignation } =
      body;
    return api.proLaborLevelDesignationApi.proLaborLevelDesignationProLaborLevelDesignationIdPut(
      {
        proLaborLevelDesignationId: proLaborLevelDesignationId,
        updateProLaborLevelDesignation,
      }
    );
  };

// Hook to mutate/update labor level designation
export const useUpdateProLaborLevelDesignation = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(updateProLaborLevelDesignation(api), {
    onSuccess: async () => {
      queryClient
        .invalidateQueries([
          ProLaborLevelDesignationCacheKeys.ProLaborLevelDesignations,
        ])
        .catch(noop);
    },
    onError: async (error) => {
      const message =
        get(error, 'response.data.message', '') || get(error, 'message', '');
      notifyError(message);
    },
  });
};
